/**
 * Authentication Context for the Student Immunization Portal
 * Provides authentication state and methods to all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { 
  Guardian, 
  AuthSession, 
  ContactMethod, 
  OTPRequest, 
  OTPVerification,
  IDVerificationRequest,
  IDVerificationResult 
} from '../types/Guardian';
import {
  requestOTP as apiRequestOTP,
  verifyOTP as apiVerifyOTP,
  verifyIDDocument as verifyIDService,
  getCurrentSession,
  logout as apiLogout,
  refreshSession,
  clearLocalSession as clearSession,
  isSessionExpired
} from '../services/authService';
import { fetchMyProfile } from '../services/guardianProfileService';
import i18n from '../i18n';
import { AppError, normalizeError } from '../utils/errorHandling';
import { getRequestLanguage } from '../utils/languagePreference';

/**
 * Auth state interface
 */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  guardian: Guardian | null;
  session: AuthSession | null;
  error: AppError | null;
  pendingOTPMethod: ContactMethod | null;
  pendingOTPDestination: string | null;
}

/**
 * Auth context interface
 */
export interface AuthContextValue extends AuthState {
  // OTP Authentication
  requestOTP: (method: ContactMethod, destination: string) => Promise<boolean>;
  verifyOTP: (code: string) => Promise<boolean>;
  cancelOTP: () => void;
  
  // ID Verification
  verifyWithID: (idImage: string, selfieImage?: string) => Promise<IDVerificationResult>;
  
  // Session management
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;

  // Guardian profile (plan §5.7.4)
  setGuardian: (guardian: Guardian) => void;
  refreshGuardian: () => Promise<Guardian | null>;
  
  // Kiosk mode helpers
  isKioskMode: boolean;
  setKioskMode: (enabled: boolean) => void;
}

/**
 * Default context value
 */
const defaultContextValue: AuthContextValue = {
  isAuthenticated: false,
  isLoading: true,
  guardian: null,
  session: null,
  error: null,
  pendingOTPMethod: null,
  pendingOTPDestination: null,
  requestOTP: async () => false,
  verifyOTP: async () => false,
  cancelOTP: () => {},
  verifyWithID: async () => ({ verified: false, confidence: 0 }),
  logout: async () => {},
  refreshSession: async () => {},
  clearError: () => {},
  setGuardian: () => {},
  refreshGuardian: async () => null,
  isKioskMode: false,
  setKioskMode: () => {},
};

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextValue>(defaultContextValue);

/**
 * Auth provider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth provider component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    guardian: null,
    session: null,
    error: null,
    pendingOTPMethod: null,
    pendingOTPDestination: null,
  });
  
  const [isKioskMode, setKioskMode] = useState<boolean>(() => {
    // Check URL parameter or environment variable
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('mode') === 'kiosk' || import.meta.env.VITE_APP_MODE === 'kiosk';
  });

  /**
   * Initialize auth state from stored session
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { session, guardian } = await getCurrentSession();
        
        if (session && !isSessionExpired(session)) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            guardian,
            session,
            error: null,
            pendingOTPMethod: null,
            pendingOTPDestination: null,
          });
        } else {
          // Clear expired session
          clearSession();
          setState({
            isAuthenticated: false,
            isLoading: false,
            guardian: null,
            session: null,
            error: null,
            pendingOTPMethod: null,
            pendingOTPDestination: null,
          });
        }
      } catch (error) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          guardian: null,
          session: null,
          error: normalizeError(error),
          pendingOTPMethod: null,
          pendingOTPDestination: null,
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Request OTP for authentication
   */
  const requestOTP = useCallback(async (method: ContactMethod, destination: string): Promise<boolean> => {
    setState(prev => ({ ...prev, error: null }));
    
    try {
      await apiRequestOTP(destination, method);
      
      setState(prev => ({
        ...prev,
        pendingOTPMethod: method,
        pendingOTPDestination: destination,
      }));
      
      return true;
    } catch (error) {
      setState(prev => ({ ...prev, error: normalizeError(error) }));
      return false;
    }
  }, []);

  /**
   * Verify OTP code
   */
  const verifyOTP = useCallback(async (code: string): Promise<boolean> => {
    if (!state.pendingOTPMethod || !state.pendingOTPDestination) {
      setState(prev => ({
        ...prev,
        error: new AppError({
          code: 'VALIDATION_ERROR' as any,
          message: 'No pending OTP request',
          userMessage: 'Please request a new code first.',
        }),
      }));
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await apiVerifyOTP(
        state.pendingOTPDestination,
        code,
        state.pendingOTPMethod
      );

      if (result.success && result.session && result.guardian) {
        // Store guardian info
        localStorage.setItem('guardian', JSON.stringify(result.guardian));
        
        setState({
          isAuthenticated: true,
          isLoading: false,
          guardian: result.guardian,
          session: result.session,
          error: null,
          pendingOTPMethod: null,
          pendingOTPDestination: null,
        });
        
        return true;
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: new AppError({
            code: 'AUTHENTICATION_ERROR' as any,
            message: 'OTP verification failed',
            userMessage: result.message || 'Invalid code. Please try again.',
          }),
        }));
        return false;
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: normalizeError(error),
      }));
      return false;
    }
  }, [state.pendingOTPMethod, state.pendingOTPDestination]);

  /**
   * Cancel pending OTP
   */
  const cancelOTP = useCallback((): void => {
    setState(prev => ({
      ...prev,
      pendingOTPMethod: null,
      pendingOTPDestination: null,
      error: null,
    }));
  }, []);

  /**
   * Verify with ID document
   */
  const verifyWithID = useCallback(async (
    idImage: string,
    selfieImage?: string
  ): Promise<IDVerificationResult> => {
    console.log('AuthContext: Starting ID verification with images', {
      hasIdImage: !!idImage,
      idImageType: typeof idImage,
      idImageLength: idImage?.length,
      hasSelfieImage: !!selfieImage,
      selfieImageLength: selfieImage?.length
    });

    // Validate image format
    if (typeof idImage !== 'string' || !idImage) {
      console.error('AuthContext: Invalid idImage format', { idImage, type: typeof idImage });
      return {
        verified: false,
        confidence: 0,
        error: 'Invalid image format. Please try taking the photo again.'
      };
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const request: IDVerificationRequest = {
        documentImage: idImage,
        documentType: "national_id",
        selfieImage: selfieImage,
        preferredLanguage: getRequestLanguage()
      };

      console.log('AuthContext: Sending ID verification request', {
        documentType: request.documentType,
        hasDocumentImage: !!request.documentImage,
        documentImageLength: request.documentImage?.length,
        hasSelfieImage: !!request.selfieImage
      });

      console.log('AuthContext: About to call verifyIDService');
      const result = await verifyIDService(request);
      console.log('AuthContext: verifyIDService returned', result);

      console.log('AuthContext: ID verification response', {
        success: result.success,
        verified: result.result?.verified,
        hasSession: !!result.session,
        error: result.result?.error
      });

      if (result.success && result.result?.verified && result.session) {
        // Store guardian info
        localStorage.setItem('guardian', JSON.stringify(result.guardian));

        setState({
          isAuthenticated: true,
          isLoading: false,
          guardian: result.guardian,
          session: result.session,
          error: null,
          pendingOTPMethod: null,
          pendingOTPDestination: null,
        });
      } else {
        // The backend returns a stable code for a photo with no readable
        // document number; never show the raw code to the user.
        if (result.result?.error === 'ID_UNREADABLE') {
          result.result.error = i18n.t('auth.idVerification.unreadable');
        }
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.result?.error ? new AppError({
            code: 'AUTHENTICATION_ERROR' as any,
            message: result.result.error,
            userMessage: result.result.error,
          }) : null,
        }));
      }

      return result.result || { verified: false, confidence: 0 };
    } catch (error) {
      console.error('AuthContext: ID verification failed', error);
      const appError = normalizeError(error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: appError,
      }));
      return {
        verified: false,
        confidence: 0,
        error: appError.userMessage,
      };
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
    } catch {
      // Ignore logout errors
    }

    clearSession();
    localStorage.removeItem('guardian');

    // Clear all storage for kiosk mode
    if (isKioskMode) {
      sessionStorage.clear();
      localStorage.clear();
    }

    setState({
      isAuthenticated: false,
      isLoading: false,
      guardian: null,
      session: null,
      error: null,
      pendingOTPMethod: null,
      pendingOTPDestination: null,
    });
  }, [isKioskMode]);

  /**
   * Refresh session
   */
  const refreshSessionCallback = useCallback(async (): Promise<void> => {
    if (!state.session) return;

    try {
      const newSession = await refreshSession();
      setState(prev => ({ ...prev, session: newSession }));
    } catch (error) {
      // Session refresh failed, logout
      await logout();
    }
  }, [state.session, logout]);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Store an updated guardian (e.g. after completing the profile gate).
   */
  const setGuardian = useCallback((guardian: Guardian): void => {
    localStorage.setItem('guardian', JSON.stringify(guardian));
    setState(prev => ({ ...prev, guardian }));
  }, []);

  /**
   * Re-read the guardian from the server. Null on failure (401 is handled by
   * the api interceptor).
   */
  const refreshGuardian = useCallback(async (): Promise<Guardian | null> => {
    try {
      const guardian = await fetchMyProfile();
      setGuardian(guardian);
      return guardian;
    } catch {
      return null;
    }
  }, [setGuardian]);

  /**
   * Context value
   */
  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    requestOTP,
    verifyOTP,
    cancelOTP,
    verifyWithID,
    logout,
    refreshSession: refreshSessionCallback,
    clearError,
    setGuardian,
    refreshGuardian,
    isKioskMode,
    setKioskMode,
  }), [
    state,
    requestOTP,
    verifyOTP,
    cancelOTP,
    verifyWithID,
    logout,
    refreshSessionCallback,
    clearError,
    setGuardian,
    refreshGuardian,
    isKioskMode,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * HOC to protect routes requiring authentication
 */
export const withAuth = <P extends object>(
  WrappedComponent: React.ComponentType<P>
): React.FC<P> => {
  const WithAuthComponent: React.FC<P> = (props) => {
    const { isAuthenticated, isLoading } = useAuthContext();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = '/login';
      return null;
    }

    return <WrappedComponent {...props} />;
  };

  WithAuthComponent.displayName = `WithAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  
  return WithAuthComponent;
};

export default AuthContext;